export const DEFAULT_COLOR = '4E5DCA';

/**
 * Converts rgb()/rgba() to uppercase hexadecimal.
 *
 * rgb(255, 0, 0)         -> FF0000
 * rgba(255, 0, 0, 0.5)   -> FF000080
 */
export const rgbToHex = (color: string): string => {
  const match = color
    .trim()
    .match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i);

  if (!match) {
    throw new Error(`Invalid RGB/RGBA color: "${color}"`);
  }

  const [, red, green, blue, alpha] = match;

  const rgb = [Number(red), Number(green), Number(blue)];

  if (!rgb.every((value) => Number.isInteger(value) && value >= 0 && value <= 255)) {
    throw new Error(`RGB values must be between 0 and 255.`);
  }

  const toHex = (value: number): string => value.toString(16).padStart(2, '0').toUpperCase();

  const hex = `${rgb.map(toHex).join('')}`;

  if (alpha === undefined) {
    return hex;
  }

  const alphaValue = Number(alpha);

  if (!Number.isFinite(alphaValue) || alphaValue < 0 || alphaValue > 1) {
    throw new Error(`Alpha value must be between 0 and 1.`);
  }

  return `${hex}${toHex(Math.round(alphaValue * 255))}`;
};

export const toHexCodeOrDefaultColor = (color: string | null | undefined): string => {
  try {
    if (color?.trim()) {
      const trimmed = color.trim();

      if (/^rgba?\(/i.test(trimmed)) {
        return rgbToHex(trimmed);
      }

      const normalized = trimmed.replace(/^#/, '');

      if (/^[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(normalized)) {
        return `${normalized.toUpperCase()}`;
      }
    }
  } catch {
    // Fall back to the default color.
  }

  return DEFAULT_COLOR;
};

export const getValidHexCode = (colorValue?: string, fallbackColor?: string): string => {
  return colorValue && /^[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(colorValue)
    ? colorValue.toUpperCase()
    : toHexCodeOrDefaultColor(fallbackColor);
};
